// The subset of @livekit/components-react the Fluxer client uses, on top of
// parley's MoQ-backed Room. Names and shapes follow LiveKit's components.
import type React from 'react';
import {createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {ParticipantEvent, RoomEvent, TrackEvent} from './enums';
import type {Participant} from './participant';
import type {TrackPublication} from './publication';
import type {Room} from './room';
import {RemoteAudioTrack, Track} from './track';

export interface TrackReference {
	participant: Participant;
	publication: TrackPublication;
	source: Track.Source;
}

export interface TrackReferencePlaceholder {
	participant: Participant;
	publication?: undefined;
	source: Track.Source;
}

export type TrackReferenceOrPlaceholder = TrackReference | TrackReferencePlaceholder;

export function isTrackReference(ref: unknown): ref is TrackReference {
	return typeof ref === 'object' && ref !== null && (ref as TrackReference).publication !== undefined;
}

export const RoomContext = createContext<Room | undefined>(undefined);
export const ParticipantContext = createContext<Participant | undefined>(undefined);
export const TrackRefContext = createContext<TrackReferenceOrPlaceholder | undefined>(undefined);

export function useMaybeRoomContext(): Room | undefined {
	return useContext(RoomContext);
}

export function useRoomContext(): Room {
	const room = useContext(RoomContext);
	if (!room) throw new Error('useRoomContext used outside RoomContext');
	return room;
}

const ALL_ROOM_EVENTS: Array<RoomEvent> = [
	RoomEvent.ParticipantConnected,
	RoomEvent.ParticipantDisconnected,
	RoomEvent.ConnectionStateChanged,
	RoomEvent.LocalTrackPublished,
	RoomEvent.LocalTrackUnpublished,
	RoomEvent.TrackPublished,
	RoomEvent.TrackUnpublished,
	RoomEvent.TrackSubscriptionStatusChanged,
	RoomEvent.TrackSubscribed,
	RoomEvent.TrackUnsubscribed,
	RoomEvent.TrackMuted,
	RoomEvent.TrackUnmuted,
];

/** Re-render whenever one of the given room events fires. */
function useRoomVersion(room: Room | undefined, events: Array<RoomEvent>): number {
	const [version, setVersion] = useState(0);
	const key = events.join(',');
	useEffect(() => {
		if (!room) return;
		const bump = () => setVersion((v) => v + 1);
		for (const e of events) room.on(e, bump);
		return () => {
			for (const e of events) room.off(e, bump);
		};
	}, [room, key]);
	return version;
}

type SourceSpec = Track.Source | {source: Track.Source; withPlaceholder: boolean};

export interface UseTracksOptions {
	updateOnlyOn?: Array<RoomEvent>;
	onlySubscribed?: boolean;
	room?: Room;
}

export function useTracks(
	sources: Array<SourceSpec> = [
		Track.Source.Camera,
		Track.Source.Microphone,
		Track.Source.ScreenShare,
		Track.Source.ScreenShareAudio,
	],
	options: UseTracksOptions = {},
): Array<TrackReferenceOrPlaceholder> {
	const contextRoom = useMaybeRoomContext();
	const room = options.room ?? contextRoom;
	const version = useRoomVersion(room, options.updateOnlyOn ?? ALL_ROOM_EVENTS);
	const key = sources.map((s) => (typeof s === 'string' ? s : `${s.source}:${s.withPlaceholder}`)).join('|');
	return useMemo(() => {
		if (!room) return [];
		const participants: Array<Participant> = [room.localParticipant, ...room.remoteParticipants.values()];
		const out: Array<TrackReferenceOrPlaceholder> = [];
		for (const participant of participants) {
			for (const spec of sources) {
				const source = typeof spec === 'string' ? spec : spec.source;
				const placeholder = typeof spec === 'string' ? false : spec.withPlaceholder;
				const publication = participant.getTrackPublication(source);
				if (publication && (!options.onlySubscribed || publication.isSubscribed)) {
					out.push({participant, publication, source});
				} else if (placeholder) {
					out.push({participant, source});
				}
			}
		}
		return out;
	}, [room, version, key, options.onlySubscribed]);
}

export interface VideoTrackProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
	trackRef?: TrackReference;
	onTrackClick?: (evt: {participant: Participant; track: TrackPublication}) => void;
	onSubscriptionStatusChanged?: (subscribed: boolean) => void;
	manageSubscription?: boolean;
}

export const VideoTrack = forwardRef<HTMLVideoElement, VideoTrackProps>(function VideoTrack(
	{trackRef, onTrackClick, onSubscriptionStatusChanged, manageSubscription, onClick, ...props},
	ref,
) {
	const contextRef = useContext(TrackRefContext);
	const reference = trackRef ?? (isTrackReference(contextRef) ? contextRef : undefined);
	const elementRef = useRef<HTMLVideoElement | null>(null);
	const setRefs = useCallback(
		(el: HTMLVideoElement | null) => {
			elementRef.current = el;
			if (typeof ref === 'function') ref(el);
			else if (ref) ref.current = el;
		},
		[ref],
	);
	const track = reference?.publication.track;
	const [, setTick] = useState(0);
	useEffect(() => {
		const pub = reference?.publication;
		if (!pub) return;
		const bump = () => setTick((t) => t + 1);
		pub.on(TrackEvent.Subscribed, bump);
		pub.on(TrackEvent.Unsubscribed, bump);
		return () => {
			pub.off(TrackEvent.Subscribed, bump);
			pub.off(TrackEvent.Unsubscribed, bump);
		};
	}, [reference?.publication]);
	useEffect(() => {
		const el = elementRef.current;
		if (!el || !track) return;
		track.attach(el);
		onSubscriptionStatusChanged?.(true);
		return () => {
			track.detach(el);
		};
	}, [track, onSubscriptionStatusChanged]);
	useEffect(() => {
		if (!manageSubscription || !reference) return;
		const pub = reference.publication as TrackPublication & {setSubscribed?: (s: boolean) => void};
		pub.setSubscribed?.(true);
		return () => pub.setSubscribed?.(false);
	}, [manageSubscription, reference]);
	return (
		<video
			ref={setRefs}
			muted
			playsInline
			autoPlay
			{...props}
			onClick={(evt) => {
				onClick?.(evt);
				if (reference) onTrackClick?.({participant: reference.participant, track: reference.publication});
			}}
		/>
	);
});

export interface UseParticipantTileProps<T extends HTMLElement = HTMLElement> {
	trackRef?: TrackReferenceOrPlaceholder;
	htmlProps: React.HTMLAttributes<T>;
	onParticipantClick?: (evt: {participant: Participant; track?: TrackPublication}) => void;
	disableSpeakingIndicator?: boolean;
}

/** Data attributes LiveKit's tiles expose (speaking, muted, source). */
export function useParticipantTile<T extends HTMLElement = HTMLElement>({
	trackRef,
	htmlProps,
	onParticipantClick,
	disableSpeakingIndicator,
}: UseParticipantTileProps<T>): {elementProps: React.HTMLAttributes<T>} {
	const contextRef = useContext(TrackRefContext);
	const ref = trackRef ?? contextRef;
	const participant = ref?.participant;
	const [, setTick] = useState(0);
	useEffect(() => {
		if (!participant) return;
		const bump = () => setTick((t) => t + 1);
		const events = [ParticipantEvent.IsSpeakingChanged, ParticipantEvent.TrackMuted, ParticipantEvent.TrackUnmuted];
		for (const e of events) participant.on(e, bump);
		return () => {
			for (const e of events) participant.off(e, bump);
		};
	}, [participant]);
	const micPub = participant?.getTrackPublication(Track.Source.Microphone);
	const elementProps = {
		...htmlProps,
		className: ['lk-participant-tile', htmlProps.className].filter(Boolean).join(' '),
		'data-lk-audio-muted': micPub ? micPub.isMuted : true,
		'data-lk-video-muted': ref?.publication ? ref.publication.isMuted : true,
		'data-lk-speaking': disableSpeakingIndicator ? false : (participant?.isSpeaking ?? false),
		'data-lk-local-participant': participant?.isLocal ?? false,
		'data-lk-source': ref?.source,
		onClick: (evt: React.MouseEvent<T>) => {
			htmlProps.onClick?.(evt);
			if (participant) onParticipantClick?.({participant, track: ref?.publication});
		},
	} as React.HTMLAttributes<T>;
	return {elementProps};
}

export function useAudioPlayback(room?: Room): {canPlayAudio: boolean; startAudio: () => Promise<void>} {
	const contextRoom = useMaybeRoomContext();
	const target = room ?? contextRoom;
	const [canPlayAudio, setCanPlayAudio] = useState(target?.canPlaybackAudio ?? true);
	useEffect(() => {
		if (!target) return;
		const update = () => setCanPlayAudio(target.canPlaybackAudio);
		target.on(RoomEvent.AudioPlaybackStatusChanged, update);
		return () => {
			target.off(RoomEvent.AudioPlaybackStatusChanged, update);
		};
	}, [target]);
	const startAudio = useCallback(async () => {
		await target?.startAudio();
	}, [target]);
	return {canPlayAudio, startAudio};
}

export interface RoomAudioRendererProps {
	room?: Room;
	volume?: number;
	muted?: boolean;
}

/** Plays every remote audio track through its own <audio> element. */
export function RoomAudioRenderer({room, volume, muted}: RoomAudioRendererProps): React.ReactElement {
	const tracks = useTracks([Track.Source.Microphone, Track.Source.ScreenShareAudio], {room, onlySubscribed: true});
	return (
		<div style={{display: 'none'}}>
			{tracks.map((ref) =>
				isTrackReference(ref) && !ref.participant.isLocal ? (
					<AudioTrack key={ref.publication.trackSid} trackRef={ref} volume={volume} muted={muted} />
				) : null,
			)}
		</div>
	);
}

function AudioTrack({
	trackRef,
	volume,
	muted,
}: {
	trackRef: TrackReference;
	volume?: number;
	muted?: boolean;
}): React.ReactElement {
	const ref = useRef<HTMLAudioElement | null>(null);
	const track = trackRef.publication.track;
	useEffect(() => {
		const el = ref.current;
		if (!el || !track) return;
		track.attach(el);
		return () => {
			track.detach(el);
		};
	}, [track]);
	useEffect(() => {
		const el = ref.current;
		if (!el || !track) return;
		// Mixed audio plays in the shared context: the element stays muted and
		// volume and deafen go to the mix (RemoteAudioTrack.webAudioMixed).
		if (track instanceof RemoteAudioTrack && track.webAudioMixed) {
			el.muted = true;
			track.setOutput(volume ?? 1, !!muted);
			return;
		}
		if (volume !== undefined) el.volume = Math.min(1, Math.max(0, volume));
		el.muted = !!muted;
	}, [track, volume, muted]);
	// biome-ignore lint/a11y/useMediaCaption: hidden sink for live voice audio; there is no caption source
	return <audio ref={ref} autoPlay />;
}
